import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import AllocationList from './AllocationList';
import useStore from '../useStore';
import { useNavigate } from 'react-router-dom';

function LoadingIndicator() {
    return (
        <div className="loading-container">
            <div className="spinner"></div>
            <div className="loading-label">Loading</div>
        </div>
    );
}

function CheckList({ agency, onBack }) {
    const [loading, setLoading] = useState(true);
    const [checks, setChecks] = useState([]);
    const [error, setError] = useState(null);
    const [selectedCheck, setSelectedCheck] = useState(null);
    const [isSystemUser, setIsSystemUser] = useState(false);

    const { selectedAgency, setSelectedAgency, userEmail } = useStore();
    const navigate = useNavigate();

    // ✅ Ensure agency context is set
    useEffect(() => {
        if (!selectedAgency && agency) {
            setSelectedAgency(agency);
        }
    }, [agency, selectedAgency, setSelectedAgency]);

    // ✅ Check if user is system user
    useEffect(() => {
        if (!userEmail) return;
        axios.get(`${process.env.REACT_APP_API_URL}/SystemUser?email=${userEmail}`)
            .then((response) => {
                // CRM system users return a valid GUID, not all zeros
                if (response.data.systemUserId !== "00000000-0000-0000-0000-000000000000") {
                    setIsSystemUser(true);
                }
            })
            .catch((error) => {
                console.error("Error checking system user:", error);
                setIsSystemUser(false);
            });
    }, [userEmail]);

    // ✅ Load checks for selected agency
    useEffect(() => {
        const currentAgency = selectedAgency || agency;
        if (!currentAgency) return;

        setLoading(true);
        axios.get(`${process.env.REACT_APP_API_URL}/Checks?vendorid=${currentAgency.vendorId}`)
            .then((response) => {
                const groupedChecks = response.data.reduce((acc, check) => {
                    const existing = acc.find(item => item.checkNumber === check.checkNumber);
                    if (existing) {
                        existing.netPayment = parseFloat(existing.netPayment) + parseFloat(check.netPayment);
                    } else {
                        acc.push({ ...check, id: check.id || acc.length });
                    }
                    return acc;
                }, []);
                const sortedChecks = groupedChecks.sort((a, b) => new Date(b.checkDate) - new Date(a.checkDate));
                setChecks(sortedChecks);
            })
            .catch((error) => {
                console.error('Error loading checks:', error);
                setError(error.response?.data || error.message);
            })
            .finally(() => setLoading(false));
    }, [agency, selectedAgency]);

    const handleBack = () => {
        if (isSystemUser) {
            navigate('/admin-portal'); // Redirect if system user
        } else {
            onBack(); // Otherwise go back to agency list
        }
    };

    const columns = [
        {
            field: 'checkNumber', headerName: 'Payment Reference', width: 150,
            renderCell: (params) => (
                <span style={{ color: 'red', cursor: 'pointer' }}>
                    {params.value}
                </span>
            )
        },
        {
            field: 'netPayment',
            headerName: 'Amount',
            width: 150,
            valueFormatter: (params) => {
                const value = params.value;

                // Check if value contains *
                if (typeof value === 'string' && value.includes('*')) {
                    return '*****';
                }

                // Otherwise format as currency
                const v = parseFloat(params); 
                if (isNaN(v)) return '*****';
                return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
            }
        },
        { field: 'payoutVoucherId', headerName: 'PayVoucherId', width: 150 },
        {
            field: 'checkDate',
            headerName: 'Payment Date',
            width: 150,
            sortable: true,
            valueFormatter: (params) => {
                const date = new Date(params.value);
                return isNaN(date.getTime()) ? params.value : date.toLocaleDateString();
            },
        },
    ];

    const handleRowClick = (params) => {
        setSelectedCheck(params.row);
    };

    const columnVisibilityModel = {
        payoutVoucherId: false,
        checkDate: true,
    };

    if (loading) return <LoadingIndicator />;
    if (error) return <div>Error: {error.message}</div>;

    return (
        <div>
            {selectedCheck ? (
                <AllocationList check={selectedCheck} onBack={() => setSelectedCheck(null)} />
            ) : (
                <div style={{ height: 400, width: '100%' }}>
                    <button className="gradient-button" onClick={handleBack}>
                        Back to Agencies
                    </button>
                    <h2>Payments for {agency?.name}</h2>
                    <DataGrid
                        rows={checks}
                        columns={columns}
                        pageSize={5}
                        onRowClick={handleRowClick}
                        columnVisibilityModel={columnVisibilityModel}
                    />
                </div>
            )}
        </div>
    );
}

export default CheckList;